import type { z } from 'zod';

import { API, buildRequestHeaders } from '@pray/shared/utils/api/api';
import { ApiProblemError, toApiProblem } from '@pray/shared/utils/api/moduleApi';
import { authenticate } from '@pray/shared/utils/auth';

export const OFFER_FUNNEL_TIMEOUT_MS = 15_000;

type Method = 'get' | 'post' | 'put' | 'patch' | 'delete';

type RequestOptions = {
  params?: Record<string, unknown>;
  data?: Record<string, unknown>;
  headers?: Record<string, string>;
};

type HttpFailure = { code?: string; response?: { status: number; data?: unknown } };

/** A failure with no HTTP answer still becomes a problem, so callers map one error type to copy. */
export function toOfferFunnelProblem(error: unknown): ApiProblemError {
  if (error instanceof ApiProblemError) return error;
  const failure = error as HttpFailure;
  if (failure?.response) return toApiProblem(failure.response.status, failure.response.data);
  const code = failure?.code === 'ECONNABORTED' || failure?.code === 'ETIMEDOUT' ? 'timeout' : 'network';
  return new ApiProblemError({ status: 0, code, title: code, detail: null, requestId: null, errors: [] });
}

function contractMismatch(issues: string): ApiProblemError {
  return new ApiProblemError({
    status: 0,
    code: 'contract_mismatch',
    title: 'contract_mismatch',
    detail: issues,
    requestId: null,
    errors: [],
  });
}

async function send(method: Method, url: string, { params, data, headers }: RequestOptions) {
  const response = await API.request({
    method,
    url,
    params,
    data,
    timeout: OFFER_FUNNEL_TIMEOUT_MS,
    responseType: 'json',
    headers: { ...buildRequestHeaders(), ...headers },
  });
  return response.data as unknown;
}

async function sendOnceReauthenticated(method: Method, url: string, options: RequestOptions) {
  try {
    return await send(method, url, options);
  } catch (error) {
    if ((error as HttpFailure)?.response?.status !== 401) throw toOfferFunnelProblem(error);
    await authenticate().catch(() => undefined);
    return send(method, url, options).catch((retryError) => {
      throw toOfferFunnelProblem(retryError);
    });
  }
}

/** Every SUPPORT-1634 call: timeout, one re-auth on 401, problem mapping, and the wire contract. */
export async function requestContract<T extends z.ZodTypeAny>(
  schema: T,
  method: Method,
  url: string,
  options: RequestOptions = {}
): Promise<z.infer<T>> {
  const body = await sendOnceReauthenticated(method, url, options);
  const parsed = schema.safeParse(body);
  if (!parsed.success) throw contractMismatch(parsed.error.message);
  return parsed.data;
}
